import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  IFavoriteRepository,
  FavoriteTargetType,
  FavoriteFilter,
  GetFavoriteData,
} from './interfaces/favorite.repository.interface';
import { profileDocRef } from "@/lib/public-profile";

function isPermissionDenied(error: unknown): boolean {
  return error instanceof FirebaseError && error.code === 'permission-denied';
}

/**
 * Resolves the Firestore collection name for a given target type.
 * Professional favorites historically lived in a separate collection.
 */
function getCollectionForType(targetType: FavoriteTargetType): string {
  return targetType === 'professional' ? 'professionalFavorites' : 'favorites';
}

/**
 * Maps the Firestore collection that stores the original target entity.
 */
function getTargetCollectionName(targetType: FavoriteTargetType): string | null {
  switch (targetType) {
    case 'course':
      return 'courses';
    case 'instructor':
      return 'users';
    case 'professional':
      return 'professionalProfiles';
    case 'job':
      return 'jobs';
    case 'resource':
      return 'resources';
    case 'event':
      return 'events';
    default:
      return null;
  }
}

export class FavoriteRepository implements IFavoriteRepository {
  // ---------------------------------------------------------------------------
  // Core operations
  // ---------------------------------------------------------------------------

  async addFavorite(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<string> {
    try {
      const colName = getCollectionForType(targetType);
      const colRef = collection(db, colName);

      // Check if already favorited
      const already = await this.checkIfFavorited(userId, targetId, targetType);
      if (already) {
        throw new DRXError({
          code: 'FAVORITE_ALREADY_EXISTS',
          devMessage: `User ${userId} already favorited ${targetType}/${targetId}`,
          userMessage: 'This item is already in your favorites.',
          level: 'warning',
        });
      }

      // Build the document payload
      const payload: Record<string, unknown> = {
        userId,
        createdAt: serverTimestamp(),
      };

      if (targetType === 'professional') {
        // Legacy professionalFavorites schema uses `professionalId`
        payload.professionalId = targetId;
      } else {
        payload.targetId = targetId;
        payload.targetType = targetType;
      }

      // Cache lightweight target data (title/image) for ALL types — including
      // professionals — so the favorites dropdown/page can render a name
      // without an extra read per row.
      const targetData = await this.fetchTargetData(targetId, targetType);
      if (targetData) {
        payload.targetData = targetData;
      }

      const docRef = await addDoc(colRef, payload);
      return docRef.id;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'FAVORITE_ADD_FAILED',
        devMessage: `Failed to add favorite ${targetType}/${targetId} for user ${userId}`,
        userMessage: 'Could not add to favorites. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async removeFavorite(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<void> {
    try {
      const colName = getCollectionForType(targetType);
      const colRef = collection(db, colName);

      const constraints =
        targetType === 'professional'
          ? [where('userId', '==', userId), where('professionalId', '==', targetId)]
          : [
              where('userId', '==', userId),
              where('targetId', '==', targetId),
              where('targetType', '==', targetType),
            ];

      const q = query(colRef, ...constraints);
      const snapshot = await getDocs(q);

      const deletePromises = snapshot.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'FAVORITE_REMOVE_FAILED',
        devMessage: `Failed to remove favorite ${targetType}/${targetId} for user ${userId}`,
        userMessage: 'Could not remove from favorites. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async toggleFavorite(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<boolean> {
    try {
      const isFavorited = await this.checkIfFavorited(userId, targetId, targetType);

      if (isFavorited) {
        await this.removeFavorite(userId, targetId, targetType);
        return false;
      } else {
        await this.addFavorite(userId, targetId, targetType);
        return true;
      }
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'FAVORITE_TOGGLE_FAILED',
        devMessage: `Failed to toggle favorite ${targetType}/${targetId} for user ${userId}`,
        userMessage: 'Could not update favorite status. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async checkIfFavorited(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<boolean> {
    try {
      const colName = getCollectionForType(targetType);
      const colRef = collection(db, colName);

      const constraints =
        targetType === 'professional'
          ? [where('userId', '==', userId), where('professionalId', '==', targetId)]
          : [
              where('userId', '==', userId),
              where('targetId', '==', targetId),
              where('targetType', '==', targetType),
            ];

      const q = query(colRef, ...constraints, limit(1));
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      throw new DRXError({
        code: 'FAVORITE_CHECK_FAILED',
        devMessage: `Failed to check favorite status for ${targetType}/${targetId}, user ${userId}`,
        userMessage: 'Could not check favorite status.',
        cause: error,
        level: 'error',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Query methods
  // ---------------------------------------------------------------------------

  async getUserFavorites(
    userId: string,
    filter?: FavoriteFilter,
  ): Promise<GetFavoriteData[]> {
    if (filter?.targetType === 'professional') {
      return this.getUserProfessionalFavorites(userId, filter?.limit);
    }

    const general = await this.getGeneralFavorites(userId, filter);

    // The navbar dropdown and /favorites page call this with no targetType
    // filter and expect EVERY kind of favorite. Professional favorites live in
    // a separate `professionalFavorites` collection, so merge them in here —
    // otherwise favouriting a professional never shows up in the list.
    let rows: GetFavoriteData[];
    if (!filter?.targetType) {
      let professional: GetFavoriteData[] = [];
      try {
        professional = await this.getUserProfessionalFavorites(userId);
      } catch {
        professional = [];
      }
      const seconds = (f: GetFavoriteData) =>
        (f as GetFavoriteData & { createdAt?: { seconds?: number } }).createdAt?.seconds || 0;
      const merged = [...general, ...professional].sort((a, b) => seconds(b) - seconds(a));
      rows = filter?.limit ? merged.slice(0, filter.limit) : merged;
    } else {
      rows = general;
    }

    // Self-heal favorites saved before targetData caching existed (they render
    // as raw doc IDs in the dropdown — the reported bug). Hydrate the display
    // data now and write it back to the favorite doc (own-doc update, allowed
    // by rules) so each row is repaired exactly once.
    await this.hydrateMissingTargetData(rows);
    return rows;
  }

  /** Backfills `targetData` on rows that lack a usable title. Best-effort. */
  private async hydrateMissingTargetData(rows: GetFavoriteData[]): Promise<void> {
    const needsHydration = rows.filter((r) => !r.targetData?.title && r.targetId && r.targetType);
    if (needsHydration.length === 0) return;

    await Promise.allSettled(
      needsHydration.map(async (row) => {
        const targetData = await this.fetchTargetData(row.targetId, row.targetType);
        if (!targetData) {
          // Target doc is gone (seller deleted the resource / job removed).
          // Mark IN-MEMORY ONLY so the UI can say "No longer available" —
          // never persisted, so if data is ever restored the row recovers.
          const exists = await this.checkTargetExists(row.targetId, row.targetType);
          if (!exists) {
            row.targetData = {
              id: row.targetId,
              title: '',
              description: '',
              image: '',
              type: row.targetType,
              unavailable: true,
            };
          }
          return;
        }
        row.targetData = targetData;
        // Write-back so the next load doesn't need to hydrate again. The row
        // belongs to the current user (own-only query), so rules permit it.
        try {
          const colName = getCollectionForType(row.targetType);
          await updateDoc(doc(db, colName, row.id), { targetData });
        } catch {
          // Non-critical: display is already fixed for this render.
        }
      }),
    );
  }

  /** Fetches favorites from the main `favorites` collection (all non-professional types). */
  private async getGeneralFavorites(
    userId: string,
    filter?: FavoriteFilter,
  ): Promise<GetFavoriteData[]> {
    const colRef = collection(db, 'favorites');
    const buildConstraints = (withOrderBy: boolean): any[] => {
      const c: any[] = [where('userId', '==', userId)];
      if (filter?.targetType) c.push(where('targetType', '==', filter.targetType));
      if (withOrderBy) c.push(orderBy('createdAt', 'desc'));
      if (filter?.limit) c.push(limit(filter.limit));
      return c;
    };

    // Try the indexed query first. If the composite index is still
    // building (or hasn't been deployed yet) Firestore throws
    // FAILED_PRECONDITION — fall back to an unordered query and sort
    // client-side. This way "Could not load your favorites" never
    // surfaces just because of an index lifecycle issue.
    try {
      const snapshot = await getDocs(query(colRef, ...buildConstraints(true)));
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as GetFavoriteData[];
    } catch (orderedErr) {
      const code = (orderedErr as { code?: string } | null)?.code || '';
      const isIndexMissing = code === 'failed-precondition' || /requires an index/i.test(String((orderedErr as Error)?.message || ''));
      if (!isIndexMissing) {
        if (orderedErr instanceof DRXError) throw orderedErr;
        throw new DRXError({
          code: 'FAVORITE_FETCH_FAILED',
          devMessage: `Failed to get favorites for user ${userId}`,
          userMessage: 'Could not load your favorites. Please try again.',
          cause: orderedErr,
          level: 'error',
        });
      }
      try {
        const snap = await getDocs(query(colRef, ...buildConstraints(false)));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as GetFavoriteData[];
        rows.sort((a, b) => {
          const at = (a as GetFavoriteData & { createdAt?: { seconds?: number } }).createdAt?.seconds || 0;
          const bt = (b as GetFavoriteData & { createdAt?: { seconds?: number } }).createdAt?.seconds || 0;
          return bt - at;
        });
        return filter?.limit ? rows.slice(0, filter.limit) : rows;
      } catch (fallbackErr) {
        if (fallbackErr instanceof DRXError) throw fallbackErr;
        throw new DRXError({
          code: 'FAVORITE_FETCH_FAILED',
          devMessage: `Failed to get favorites for user ${userId} (fallback)`,
          userMessage: 'Could not load your favorites. Please try again.',
          cause: fallbackErr,
          level: 'error',
        });
      }
    }
  }

  // Counts are read from the PUBLIC `favoriteCounters/{type}_{id}` aggregate
  // docs maintained server-side by the onFavoriteCreate/Delete triggers.
  //
  // WHY: `favorites/*` rows are own-only readable (privacy — you can't
  // enumerate who liked what). The previous implementation queried the raw
  // rows across ALL users, which the rules correctly denied → it silently
  // returned 0 → every detail page showed "0 likes" even when likes existed
  // (the reported bug). Counter docs are public-read, server-only-write, and
  // a count is ONE doc-ID read per item — no scan, no index, no permission
  // issue, and O(1) regardless of how popular an item is.
  //
  // Consistency: counters are trigger-maintained (eventually consistent,
  // typically <2s behind the write). The UI covers that window with an
  // optimistic bump in FavoriteButton.
  async getTargetFavoritesCount(
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<number> {
    try {
      const counterRef = doc(db, 'favoriteCounters', `${targetType}_${targetId}`);
      const snap = await getDoc(counterRef);
      const count = snap.exists() ? Number(snap.data()?.count ?? 0) : 0;
      return Number.isFinite(count) ? Math.max(0, count) : 0;
    } catch (error) {
      // Counter doc missing (never favorited) or transient read failure —
      // a count display should degrade to 0, never break the page.
      if (isPermissionDenied(error)) return 0;
      return 0;
    }
  }

  async getFavoritesCountsForIds(
    targetIds: string[],
    targetType: FavoriteTargetType,
  ): Promise<Record<string, number>> {
    const counts: Record<string, number> = {};
    const ids = [...new Set(targetIds.map(String).filter(Boolean))];
    for (const id of ids) {
      counts[id] = 0;
    }
    if (ids.length === 0) return counts;

    // Parallel doc-ID reads of the public counter docs (no query, no index).
    // A missing doc simply means "never favorited" → stays 0.
    const results = await Promise.allSettled(
      ids.map(async (id) => {
        const snap = await getDoc(doc(db, 'favoriteCounters', `${targetType}_${id}`));
        const count = snap.exists() ? Number(snap.data()?.count ?? 0) : 0;
        return { id, count: Number.isFinite(count) ? Math.max(0, count) : 0 };
      }),
    );
    for (const r of results) {
      if (r.status === 'fulfilled') counts[r.value.id] = r.value.count;
      // rejected → leave the 0 default; counts must never break a listing page
    }
    return counts;
  }

  async getTargetFavoriters(
    targetId: string,
    targetType: FavoriteTargetType,
    limitCount?: number,
  ): Promise<string[]> {
    try {
      const colName = getCollectionForType(targetType);
      const colRef = collection(db, colName);

      const constraints: any[] =
        targetType === 'professional'
          ? [where('professionalId', '==', targetId)]
          : [where('targetId', '==', targetId), where('targetType', '==', targetType)];

      constraints.push(orderBy('createdAt', 'desc'));
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      const q = query(colRef, ...constraints);
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => d.data().userId as string);
    } catch (error) {
      throw new DRXError({
        code: 'FAVORITE_FAVORITERS_FAILED',
        devMessage: `Failed to get favoriters for ${targetType}/${targetId}`,
        userMessage: 'Could not load favoriters.',
        cause: error,
        level: 'error',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  async cleanupOrphanedFavorites(userId: string): Promise<number> {
    try {
      const favorites = await this.getUserFavorites(userId);
      let deletedCount = 0;

      for (const favorite of favorites) {
        const exists = await this.checkTargetExists(favorite.targetId, favorite.targetType);
        if (!exists) {
          await deleteDoc(doc(collection(db, 'favorites'), favorite.id));
          deletedCount++;
        }
      }

      return deletedCount;
    } catch (error) {
      throw new DRXError({
        code: 'FAVORITE_CLEANUP_FAILED',
        devMessage: `Failed to clean up orphaned favorites for user ${userId}`,
        userMessage: 'Could not clean up old favorites.',
        cause: error,
        level: 'error',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async getUserProfessionalFavorites(
    userId: string,
    limitCount?: number,
  ): Promise<GetFavoriteData[]> {
    const colRef = collection(db, 'professionalFavorites');
    const constraints: any[] = [where('userId', '==', userId)];
    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(colRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        userId: data.userId,
        targetId: data.professionalId,
        targetType: 'professional' as FavoriteTargetType,
        targetData: data.targetData,
        createdAt: data.createdAt,
      } as GetFavoriteData;
    });
  }

  private async fetchTargetData(
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<GetFavoriteData['targetData'] | null> {
    try {
      const collectionName = getTargetCollectionName(targetType);
      if (!collectionName) return null;

      const docRef = doc(db, collectionName, targetId);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) return null;

      const data = docSnapshot.data();
      // Build payload without undefined fields — Firestore addDoc rejects them by
      // default. Jobs in particular have no `price` field (they use compensation/
      // hourlyRate), so always assigning `price: data.price` produced undefined and
      // failed every job favorite with FAVORITE_ADD_FAILED.
      // Resolve a human-readable name. Most entities expose title/name/
      // displayName directly, but professionalProfiles docs do NOT store the
      // name — it's hydrated from the linked users doc at query time. So when
      // the profile doc has no name, read the user doc and compose it (matching
      // hydrateProfilesWithUserNames). Otherwise every favorited professional
      // shows up as "Unknown".
      const composeName = (d: Record<string, any>): string =>
        d.displayName ||
        d.name ||
        [d.firstName, d.lastName].filter(Boolean).join(' ').trim() ||
        d.username ||
        (typeof d.email === 'string' ? d.email.split('@')[0] : '') ||
        '';

      // Jobs store their title under `jobTitle` (not `title`) — without this a
      // favorited job fell through to composeName → "Unknown" (or the raw id in
      // the dropdown fallback). Courses/resources use `title`.
      let title = data.title || data.jobTitle || composeName(data);
      if (!title && targetType === 'professional') {
        const userId = (data.userId as string) || targetId;
        try {
          const userSnap = await getDoc(profileDocRef(userId));
          if (userSnap.exists()) {
            title = composeName(userSnap.data() as Record<string, any>);
          }
        } catch {
          // Firestore rules may block reading other users; leave title empty.
        }
      }

      const targetData: NonNullable<GetFavoriteData['targetData']> = {
        id: docSnapshot.id,
        title: title || 'Unknown',
        description: data.description || data.bio || '',
        image:
          data.thumbnailUrl ||
          data.imageUrl ||
          data.coverPhoto ||
          data.profileImageUrl ||
          '',
        type: targetType,
      };
      if (typeof data.price === 'number') {
        targetData.price = data.price;
      }
      return targetData;
    } catch {
      // Non-critical: we still create the favorite even without cached data
      return null;
    }
  }

  private async checkTargetExists(
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<boolean> {
    try {
      const collectionName = getTargetCollectionName(targetType);
      if (!collectionName) return false;

      const docRef = doc(db, collectionName, targetId);
      const docSnapshot = await getDoc(docRef);
      return docSnapshot.exists();
    } catch {
      return false;
    }
  }
}
